export const DAG_SECTION_GUTTER = 40;
export const DAG_SECTION_PADDING = 32;
export const DAG_FLOW_LABEL_WIDTH = 150;
export const DAG_FLOW_ROW_GAP = 80;
export const DAG_FLOW_ANCHOR_Y = 26;

export const getProjectDagActionsStart = (centerX, nodeWidth) => centerX + nodeWidth + DAG_SECTION_GUTTER;
export const getProjectDagSourcesEnd = (centerX) => centerX - DAG_SECTION_GUTTER;
export const isProjectDagFlowLabelBelow = (flowX, sectionStart) => sectionStart != null
  && flowX + 27 - DAG_FLOW_LABEL_WIDTH < sectionStart + DAG_SECTION_PADDING;

const isSource = (node) => ['input', 'preparation'].includes(node.data?.node?.kind);
const sourceGroupKey = (node) => node.data?.node?.sourceNodeKey || node.id;
const isFlow = (node) => node.data?.node?.nodeType === 'flow' || node.data?.node?.kind === 'output';
const actionInset = (node) => {
  const spec = node.data?.node;
  if (!isFlow(node) && !['action', 'add'].includes(spec?.nodeType) && !['action', 'add'].includes(spec?.kind)) return null;
  // Labels may move below the flow; their width must not restrict dragging.
  return DAG_SECTION_PADDING;
};
const sectionExtent = (node, minX, maxX) => node.extent?.[0]?.[0] === minX && node.extent?.[1]?.[0] === maxX
  ? node.extent : [[minX, -Infinity], [maxX, Infinity]];

// A drag preview only needs the moving node's position, not a new layout for
// every other node. Match the normal section constraints for this node.
export const constrainProjectDagDropNode = (node, center, nodeWidth) => {
  if (!node?.position || !center?.position) return node;
  let x = node.position.x;
  if (isSource(node)) {
    x = Math.min(x, getProjectDagSourcesEnd(center.position.x) - DAG_SECTION_PADDING - nodeWidth);
  } else {
    const inset = actionInset(node);
    if (inset != null) {
      x = Math.max(x, getProjectDagActionsStart(center.position.x, center.data?.node?.width || nodeWidth) + inset);
    }
  }
  return x === node.position.x ? node : { ...node, position: { ...node.position, x } };
};

// Align branch entry points, keeping each branch's internal geometry intact.
// Include the actual label height: translated names and trigger details can wrap.
export const alignProjectDagFlowRows = (nodes, flowBranches, nodeWidth, labelHeights = {}, fixedNodeKeys = new Set()) => {
  const center = nodes.find((node) => node.id === 'center');
  if (!center || !flowBranches.length) return nodes;
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const rows = flowBranches.map((branch) => {
    const members = branch.nodes.map((spec) => byId.get(spec.key)).filter(Boolean);
    const flow = members.find(isFlow);
    if (!flow) return null;
    const labelHalfHeight = (labelHeights[flow.id] || 0) / 2;
    let top = flow.position.y + DAG_FLOW_ANCHOR_Y - labelHalfHeight;
    let bottom = flow.position.y + DAG_FLOW_ANCHOR_Y + labelHalfHeight;
    members.forEach((node) => {
      if (node.hidden) return;
      top = Math.min(top, node.position.y);
      bottom = Math.max(bottom, node.position.y + (node.measured?.height || 150));
    });
    return { flow, members, top, bottom, fixed: members.some((node) => fixedNodeKeys.has(node.id)) };
  }).filter(Boolean).sort((left, right) => left.flow.position.y - right.flow.position.y);
  if (!rows.length) return nodes;
  const fixedRows = rows.filter((row) => row.fixed);
  const columnX = Math.max(
    getProjectDagActionsStart(center.position.x, center.data?.node?.width || nodeWidth)
      + DAG_SECTION_PADDING + DAG_FLOW_LABEL_WIDTH,
    (fixedRows.length ? fixedRows : rows).reduce((minimum, row) => Math.min(minimum, row.flow.position.x), Infinity)
  );
  const shifts = new Map();
  let previousBottom = fixedRows.reduce((maximum, row) => Math.max(maximum, row.bottom), -Infinity);
  rows.forEach((row) => {
    if (row.fixed) return;
    const y = Math.max(0, previousBottom + DAG_FLOW_ROW_GAP - row.top);
    const x = columnX - row.flow.position.x;
    row.members.forEach((node) => shifts.set(node.id, { x, y }));
    previousBottom = row.bottom + y;
  });
  return nodes.map((node) => {
    const shift = shifts.get(node.id);
    return !shift || (!shift.x && !shift.y) ? node : {
      ...node, position: { x: node.position.x + shift.x, y: node.position.y + shift.y },
    };
  });
};

// Keep logical ordering separate from the visual section constraints. Legacy
// layouts shift each affected branch together, preserving its relative spacing.
export const constrainProjectDagSections = (nodes, nodeWidth, {
  movingCenter = false, flowBranches = [], preserveSourceSpacing = false,
} = {}) => {
  const center = nodes.find((node) => node.id === 'center');
  if (!center) return nodes;
  const centerWidth = center.data?.node?.width || nodeWidth;
  let centerX = center.position.x;
  if (movingCenter) {
    let minX = -Infinity;
    let maxX = Infinity;
    for (const node of nodes) {
      const inset = actionInset(node);
      if (inset != null) maxX = Math.min(maxX, node.position.x - centerWidth - DAG_SECTION_GUTTER - inset);
      if (isSource(node)) minX = Math.max(minX, node.position.x + nodeWidth + DAG_SECTION_GUTTER + DAG_SECTION_PADDING);
    }
    centerX = Math.min(Math.max(centerX, minX), maxX);
  }
  const start = getProjectDagActionsStart(centerX, centerWidth);
  const sourceEnd = getProjectDagSourcesEnd(centerX) - DAG_SECTION_PADDING;
  const shifts = new Map();
  if (preserveSourceSpacing) {
    const sourceShifts = new Map();
    nodes.forEach((node) => {
      if (!isSource(node)) return;
      const key = sourceGroupKey(node);
      sourceShifts.set(key, Math.min(sourceShifts.get(key) || 0, sourceEnd - nodeWidth - node.position.x));
    });
    nodes.forEach((node) => {
      if (isSource(node)) shifts.set(node.id, sourceShifts.get(sourceGroupKey(node)));
    });
  }
  if (flowBranches.length) {
    const byId = new Map(nodes.map((node) => [node.id, node]));
    flowBranches.forEach((branch) => {
      let shift = 0;
      branch.nodes.forEach((spec) => {
        const node = byId.get(spec.key);
        const inset = node && actionInset(node);
        if (inset != null) shift = Math.max(shift, start + inset - node.position.x);
      });
      if (shift > 0) branch.nodes.forEach((spec) => shifts.set(spec.key, shift));
    });
  }
  let minCenterX = -Infinity;
  let maxCenterX = Infinity;
  const next = nodes.map((node) => {
    if (isSource(node)) {
      const x = Math.min(sourceEnd - nodeWidth, node.position.x + (shifts.get(node.id) || 0));
      minCenterX = Math.max(minCenterX, x + nodeWidth + DAG_SECTION_GUTTER + DAG_SECTION_PADDING);
      return { ...node, position: x === node.position.x ? node.position : { ...node.position, x },
        extent: sectionExtent(node, -Infinity, sourceEnd) };
    }
    const inset = actionInset(node);
    if (inset == null) return node;
    const minX = start + inset;
    const x = Math.max(minX, node.position.x + (shifts.get(node.id) || 0));
    maxCenterX = Math.min(maxCenterX, x - centerWidth - DAG_SECTION_GUTTER - inset);
    return { ...node, position: x === node.position.x ? node.position : { ...node.position, x },
      extent: sectionExtent(node, minX, Infinity) };
  });
  return next.map((node) => node.id === 'center' ? {
    ...node, position: centerX === node.position.x ? node.position : { ...node.position, x: centerX },
    // React Flow extents bound the whole node, not just its top-left corner.
    extent: sectionExtent(node, minCenterX, maxCenterX + centerWidth),
  } : node);
};
